// Stock Transfers - Step 2: Configure Transfers
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  ArrowRightLeft,
  Info,
  Save,
  Trash2,
  Plus,
  Package,
  MapPin,
} from "lucide-react";
import { useNavigation } from "@/components/layout/app-layout";
import { toast } from "sonner";
import { TransferSetupData } from "./stock-transfers-step1";
import { mockWarehouses } from "@/data/mockWarehousesLocations";

export interface TransferItem {
  itemId: string;
  itemCode: string;
  itemName: string;
  availableQuantity: number;
  sourceLocation: string;
  destinationLocation: string;
  quantity: number;
}

export interface TransferConfigData extends TransferSetupData {
  transfers: TransferItem[];
}

interface StockTransfersStep2PageProps {
  pageParams?: {
    setupData?: TransferSetupData;
  };
}

export function StockTransfersStep2Page({ pageParams }: StockTransfersStep2PageProps) {
  const { navigateTo } = useNavigation();
  const setupData = pageParams?.setupData;

  const [defaultSource, setDefaultSource] = useState("");
  const [defaultDestination, setDefaultDestination] = useState("");
  const [transfers, setTransfers] = useState<TransferItem[]>([]);

  // Initialize transfers from selected items
  useEffect(() => {
    if (setupData?.selectedItems) {
      setTransfers(
        setupData.selectedItems.map((item) => ({
          itemId: item.id,
          itemCode: item.code,
          itemName: item.name,
          availableQuantity: item.availableQuantity,
          sourceLocation: "",
          destinationLocation: "",
          quantity: 0,
        }))
      );
    }
  }, [setupData]);

  // Redirect if no setup data
  useEffect(() => {
    if (!setupData) {
      toast.error("Please complete step 1 first");
      navigateTo("stock-transfers/create/step1");
    }
  }, [setupData, navigateTo]);

  if (!setupData) {
    return null;
  }

  // Get all locations from warehouses
  const allLocations = mockWarehouses.flatMap((warehouse) =>
    warehouse.locations.map((location) => ({
      value: `${warehouse.id}-${location.id}`,
      label: `${warehouse.name} - ${location.name}`,
      warehouseId: warehouse.id,
      locationId: location.id,
    }))
  );

  const handleApplySourceToAll = () => {
    if (!defaultSource) {
      toast.error("Please select a default source location");
      return;
    }
    setTransfers(
      transfers.map((t) => ({
        ...t,
        sourceLocation: defaultSource,
      }))
    );
    toast.success("Source location applied to all items");
  };

  const handleApplyDestinationToAll = () => {
    if (!defaultDestination) {
      toast.error("Please select a default destination location");
      return;
    }
    setTransfers(
      transfers.map((t) => ({
        ...t,
        destinationLocation: defaultDestination,
      }))
    );
    toast.success("Destination location applied to all items");
  };

  const handleUpdateTransfer = (index: number, field: keyof TransferItem, value: any) => {
    const updated = [...transfers];
    updated[index] = { ...updated[index], [field]: value };
    setTransfers(updated);
  };

  const handleRemoveTransfer = (index: number) => {
    setTransfers(transfers.filter((_, i) => i !== index));
    toast.success("Item removed from transfer");
  };

  const handleAddMoreItems = () => {
    navigateTo("stock-transfers/create/step1", { setupData });
  };

  const handleBack = () => {
    navigateTo("stock-transfers/create/step1", { setupData });
  };

  const handleNext = () => {
    // Validation
    const errors: string[] = [];

    transfers.forEach((transfer, index) => {
      if (!transfer.sourceLocation) {
        errors.push(`Item ${index + 1} (${transfer.itemCode}): Source location is required`);
      }
      if (!transfer.destinationLocation) {
        errors.push(`Item ${index + 1} (${transfer.itemCode}): Destination location is required`);
      }
      if (transfer.sourceLocation === transfer.destinationLocation) {
        errors.push(`Item ${index + 1} (${transfer.itemCode}): Source and destination cannot be the same`);
      }
      if (!transfer.quantity || transfer.quantity <= 0) {
        errors.push(`Item ${index + 1} (${transfer.itemCode}): Quantity must be greater than 0`);
      }
      if (transfer.quantity > transfer.availableQuantity) {
        errors.push(`Item ${index + 1} (${transfer.itemCode}): Quantity exceeds available stock`);
      }
    });

    if (transfers.length === 0) {
      errors.push("At least one transfer is required");
    }

    if (errors.length > 0) {
      toast.error(
        <div>
          <div className="font-semibold mb-1">Please fix the following errors:</div>
          <ul className="list-disc list-inside space-y-1">
            {errors.slice(0, 3).map((error, i) => (
              <li key={i} className="text-sm">{error}</li>
            ))}
            {errors.length > 3 && (
              <li className="text-sm">...and {errors.length - 3} more</li>
            )}
          </ul>
        </div>
      );
      return;
    }

    // Navigate to step 3 with all data
    const configData: TransferConfigData = {
      ...setupData,
      transfers,
    };
    navigateTo("stock-transfers/create/step3", { configData });
  };

  const handleSaveAndExit = () => {
    // TODO: Save as draft
    toast.success("Transfer saved as draft! You can resume it later from the Stock Transfers page.");
    navigateTo("stock-transfers");
  };

  return (
    <div className="flex flex-col h-full max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-3xl font-bold tracking-tight">Create Stock Transfer</h1>
            </div>
            <p className="text-muted-foreground mt-1">
              Configure transfer details for each item
            </p>
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-600 text-white text-sm font-medium">
              ✓
            </div>
            <span className="text-sm text-muted-foreground">Select Items</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium">
              2
            </div>
            <span className="text-sm font-medium">Configure Transfers</span>
          </div>
          <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800" />
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 text-sm font-medium">
              3
            </div>
            <span className="text-sm text-muted-foreground">Review & Complete</span>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Configure transfer details
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Set the source location, destination location, and quantity for each item. Use the bulk actions to quickly apply the same locations to all items.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>
            Apply default locations to all items at once
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="default-source">Default Source Location</Label>
                <Select value={defaultSource} onValueChange={setDefaultSource}>
                  <SelectTrigger id="default-source">
                    <SelectValue placeholder="Select source..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allLocations.map((location) => (
                      <SelectItem key={location.value} value={location.value}>
                        {location.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleApplySourceToAll} variant="secondary">
                Apply to All
              </Button>
            </div>

            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-2">
                <Label htmlFor="default-destination">Default Destination Location</Label>
                <Select value={defaultDestination} onValueChange={setDefaultDestination}>
                  <SelectTrigger id="default-destination">
                    <SelectValue placeholder="Select destination..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allLocations.map((location) => (
                      <SelectItem key={location.value} value={location.value}>
                        {location.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleApplyDestinationToAll} variant="secondary">
                Apply to All
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transfer Items */}
      <Card className="flex-1">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Transfer Items</CardTitle>
              <CardDescription>
                Configure each transfer ({transfers.length} items)
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={handleAddMoreItems}>
              <Plus className="h-4 w-4 mr-2" />
              Add More Items
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {transfers.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No items to transfer. Click "Add More Items" to select items.
              </div>
            ) : (
              transfers.map((transfer, index) => (
                <Card key={transfer.itemId} className="border-2">
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Item Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <Package className="h-5 w-5 text-muted-foreground mt-0.5" />
                          <div>
                            <div className="font-semibold">{transfer.itemCode}</div>
                            <div className="text-sm text-muted-foreground">
                              {transfer.itemName}
                            </div>
                            <Badge variant="outline" className="mt-1">
                              Available: {transfer.availableQuantity}
                            </Badge>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveTransfer(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>

                      {/* Transfer Configuration */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`source-${index}`}>
                            <MapPin className="h-4 w-4 inline mr-1" />
                            From Location *
                          </Label>
                          <Select
                            value={transfer.sourceLocation}
                            onValueChange={(value) =>
                              handleUpdateTransfer(index, "sourceLocation", value)
                            }
                          >
                            <SelectTrigger id={`source-${index}`}>
                              <SelectValue placeholder="Select source..." />
                            </SelectTrigger>
                            <SelectContent>
                              {allLocations.map((location) => (
                                <SelectItem key={location.value} value={location.value}>
                                  {location.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {transfer.sourceLocation && (
                            <p className="text-xs text-muted-foreground">
                              {allLocations.find((l) => l.value === transfer.sourceLocation)?.label}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`destination-${index}`}>
                            <MapPin className="h-4 w-4 inline mr-1" />
                            To Location *
                          </Label>
                          <Select
                            value={transfer.destinationLocation}
                            onValueChange={(value) =>
                              handleUpdateTransfer(index, "destinationLocation", value)
                            }
                          >
                            <SelectTrigger id={`destination-${index}`}>
                              <SelectValue placeholder="Select destination..." />
                            </SelectTrigger>
                            <SelectContent>
                              {allLocations.map((location) => (
                                <SelectItem key={location.value} value={location.value}>
                                  {location.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {transfer.destinationLocation && (
                            <p className="text-xs text-muted-foreground">
                              {allLocations.find((l) => l.value === transfer.destinationLocation)?.label}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor={`quantity-${index}`}>Quantity *</Label>
                          <Input
                            id={`quantity-${index}`}
                            type="number"
                            min="1"
                            max={transfer.availableQuantity}
                            value={transfer.quantity || ""}
                            onChange={(e) =>
                              handleUpdateTransfer(
                                index,
                                "quantity",
                                parseInt(e.target.value) || 0
                              )
                            }
                            placeholder="0"
                          />
                          <p className="text-xs text-muted-foreground">
                            Max: {transfer.availableQuantity}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Footer Actions */}
      <div className="flex items-center justify-between mt-6 pt-6 border-t">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleSaveAndExit}>
            <Save className="h-4 w-4 mr-2" />
            Save as Draft
          </Button>
          <Button onClick={handleNext}>
            Next: Review & Complete
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
